"use client";

import React, { useState } from "react";
import { Lightbulb } from "lucide-react";

const formatCurrency = (value: number) => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number) => {
  if (value >= 1e9)
    return `${(value / 1e9)
      .toFixed(2)
      .replace(/\.00$/, "")
      .replace(/(\.[1-9])0$/, "$1")}B`;
  if (value >= 1e6)
    return `${(value / 1e6)
      .toFixed(2)
      .replace(/\.00$/, "")
      .replace(/(\.[1-9])0$/, "$1")}M`;
  if (value >= 1e3)
    return `${(value / 1e3)
      .toFixed(2)
      .replace(/\.00$/, "")
      .replace(/(\.[1-9])0$/, "$1")}K`;
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    value,
  );
};

export function WarrantSimulator() {
  const [publicUnits, setPublicUnits] = useState<number>(34500000);
  const [warrantCoverage, setWarrantCoverage] = useState<number>(0.33333333); // 1/3 coverage
  const [currentPrice, setCurrentPrice] = useState<number>(20.0); // Trigger assumes > 18.00

  // Constants
  const WARRANT_STRIKE = 11.5;

  // Math
  const totalWarrants = Math.round(publicUnits * warrantCoverage);

  // Scenario 1: Cash Exercise
  const cashProceeds = totalWarrants * WARRANT_STRIKE;
  const cashNetShares = totalWarrants;

  // Treasury Stock Method Dilution (Company buys back stock at Current Price with Proceeds)
  const repurchasedShares = cashProceeds / currentPrice;
  const tsmNetDilutiveShares = cashNetShares - repurchasedShares;

  // Scenario 2: Cashless Exercise (Make-Whole / Intrinsic Value)
  // If price <= strike, intrinsic value is 0 (warrants underwater)
  const intrinsicFraction =
    currentPrice > WARRANT_STRIKE
      ? (currentPrice - WARRANT_STRIKE) / currentPrice
      : 0;
  const cashlessNetShares = totalWarrants * intrinsicFraction;

  let insightText = "";
  if (currentPrice <= WARRANT_STRIKE) {
    insightText = `At $${currentPrice.toFixed(2)}, the warrants are out-of-the-money (below the $${WARRANT_STRIKE.toFixed(2)} strike price). There is zero dilution overhang and zero capital injected.`;
  } else {
    const dilutionDiff = Math.abs(tsmNetDilutiveShares - cashlessNetShares);
    const sharesSaved = tsmNetDilutiveShares - cashlessNetShares;

    insightText = `At $${currentPrice.toFixed(2)}, the ${formatNumber(totalWarrants)} outstanding warrants become in-the-money. A Cash Exercise would inject ${formatCurrency(cashProceeds)} while resulting in ${formatNumber(tsmNetDilutiveShares)} effective dilutive shares. Alternatively, a Cashless Exercise yields ${formatNumber(cashlessNetShares)} dilutive shares, ${sharesSaved > 0 ? `saving ${formatNumber(sharesSaved)} shares of dilution but forfeiting the cash injection.` : `costing slightly more dilution but without requiring cash outlay from holders.`}`;
  }

  const chartData = [
    {
      name: "Cash Exercise",
      "Capital Injected ($)": cashProceeds,
      "Effective Net Dilutive Shares":
        currentPrice > WARRANT_STRIKE ? tsmNetDilutiveShares : cashNetShares, // Using TSM for economic dilution
    },
    {
      name: "Cashless Exercise",
      "Capital Injected ($)": 0,
      "Effective Net Dilutive Shares": cashlessNetShares,
    },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6 bg-card rounded-2xl border border-border shadow-sm">
        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Public Units Issued
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatNumber(publicUnits)}
            </span>
          </label>
          <input
            type="range"
            min={10000000}
            max={50000000}
            step={500000}
            value={publicUnits}
            onChange={(e) => setPublicUnits(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Warrant Coverage
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {warrantCoverage > 0.34
                ? warrantCoverage > 0.6
                  ? "1:1"
                  : "1:2"
                : "1:3"}
            </span>
          </label>
          <input
            type="range"
            min={0.333333}
            max={1.0}
            step={0.166667} // roughly steps for 1/3, 1/2, 1
            value={warrantCoverage}
            onChange={(e) => setWarrantCoverage(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Current Stock Price (VWAP)
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              ${currentPrice.toFixed(2)}
            </span>
          </label>
          <input
            type="range"
            min={11.0}
            max={35.0}
            step={0.5}
            value={currentPrice}
            onChange={(e) => setCurrentPrice(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Cost Breakdown Cards */}
        <div className="space-y-4">
          <div className="p-5 bg-card rounded-xl border border-border shadow-sm">
            <h3 className="text-lg font-bold border-b border-border pb-2 mb-4">
              Cash Exercise (The "Secondary Offering")
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              When the stock crosses $18.00, Meshflow can force redemption,
              typically resulting in holders exercising for cash.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Total Warrants Exercised
                </span>
                <span className="font-mono">{formatNumber(totalWarrants)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span className="text-foreground">Gross Capital Injected</span>
                <span className="font-mono text-emerald-500">
                  {formatCurrency(cashProceeds)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground">Gross New Shares</span>
                <span className="font-mono">{formatNumber(cashNetShares)}</span>
              </div>
              <div className="flex justify-between pt-2">
                <span className="text-muted-foreground">
                  Treasury Stock Method Dilution
                </span>
                <span className="font-mono text-orange-500">
                  {formatNumber(tsmNetDilutiveShares)} shares
                </span>
              </div>
            </div>
          </div>

          <div className="p-5 bg-card rounded-xl border border-border shadow-sm">
            <h3 className="text-lg font-bold border-b border-border pb-2 mb-4">
              Cashless Exercise (Make-Whole)
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              The company can elect to force a cashless exercise, issuing a
              fraction of a share per warrant based on intrinsic value to
              preserve the cap table.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Capital Injected</span>
                <span className="font-mono">$0</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="text-muted-foreground">
                  Exchange Ratio (Fractional)
                </span>
                <span className="font-mono">
                  {intrinsicFraction.toFixed(3)} shares / warrant
                </span>
              </div>
              <div className="flex justify-between font-semibold pt-2">
                <span className="text-foreground">Net Dilutive Shares</span>
                <span className="font-mono text-orange-500">
                  {formatNumber(cashlessNetShares)} shares
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="p-6 bg-card rounded-2xl border border-border shadow-sm flex flex-col justify-center min-h-[400px]">
          <h3 className="text-xl font-bold mb-2 text-center">
            Dilution vs. Capital Injection
          </h3>
          <p className="text-sm text-center text-muted-foreground mb-6">
            Comparing the economic realities of warrant settlement.
          </p>
          <div className="w-full h-full flex flex-col justify-center gap-10 font-sans mt-8">
            {chartData.map((scenario, idx) => {
              const capital = scenario["Capital Injected ($)"];
              const shares = scenario["Effective Net Dilutive Shares"];
              // Scale proportionally up to the max possible value across all scenarios
              const maxScale =
                Math.max(
                  ...chartData.map((d) => d["Capital Injected ($)"]),
                  ...chartData.map((d) => d["Effective Net Dilutive Shares"]),
                ) || 1;

              const capPct = (capital / maxScale) * 100;
              const sharesPct = (shares / maxScale) * 100;

              return (
                <div key={idx} className="flex flex-col gap-3">
                  <span className="text-sm font-bold text-foreground block pl-28 border-l-4 border-primary ml-2">
                    {scenario.name}
                  </span>

                  {/* Capital Bar */}
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-32 text-right text-[11px] uppercase tracking-wide font-bold text-muted-foreground">
                      Capital Injected ($)
                    </div>
                    <div className="flex-1 bg-muted/20 rounded-r-md h-5 relative flex items-center">
                      <div
                        className="absolute top-0 bottom-0 left-0 bg-emerald-500 rounded-r-md transition-all duration-700 ease-out"
                        style={{ width: `${capPct}%` }}
                      ></div>
                    </div>
                    <div className="w-24 text-xs font-mono font-bold text-emerald-500">
                      {formatCurrency(capital)}
                    </div>
                  </div>

                  {/* Shares Bar */}
                  <div className="flex items-center gap-3 w-full">
                    <div className="w-32 text-right text-[11px] uppercase tracking-wide font-bold text-muted-foreground">
                      Net Dilutive Shares
                    </div>
                    <div className="flex-1 bg-muted/20 rounded-r-md h-5 relative flex items-center">
                      <div
                        className="absolute top-0 bottom-0 left-0 bg-orange-500 rounded-r-md transition-all duration-700 ease-out"
                        style={{ width: `${sharesPct}%` }}
                      ></div>
                    </div>
                    <div className="w-24 text-xs font-mono font-bold text-orange-500">
                      {formatNumber(shares)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dynamic Data Insight */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 items-start shadow-sm mt-8">
        <div className="p-3 bg-primary/10 rounded-full text-primary mt-1 flex-shrink-0">
          <Lightbulb size={24} />
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-1 text-lg">
            Key Takeaway
          </h4>
          <p className="text-muted-foreground text-lg text-balance leading-relaxed">
            {insightText}
          </p>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
